import React from 'react';
import { Badge } from '../ui/badge';
import { Scissors } from 'lucide-react';

/**
 * Shared SOAP Note Renderer — single authoritative rendering for all views.
 * Used by LiveVisit (editable), CommandCenter (review), FinalizedNote (read-only).
 */

export function NoteSection({ label, text }) {
  if (!text) return null;
  return (
    <div className="mb-5" data-testid={`note-section-${label.toLowerCase()}`}>
      <h4 className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground mb-1">{label}</h4>
      <p className="text-sm leading-relaxed whitespace-pre-wrap">{text}</p>
    </div>
  );
}

export function AssessmentPlan({ problems, fallbackAssessment, fallbackPlan }) {
  const hasProblems = (problems || []).length > 0;
  return (
    <div className="mb-5" data-testid="note-section-ap">
      <h4 className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground mb-1">Assessment & Plan</h4>
      {hasProblems ? (
        <div className="space-y-3">
          {problems.map((p, idx) => (
            <div key={idx} className="pl-1 py-1">
              <p className="text-sm font-bold">{p.problem_number || idx + 1}. {p.problem_name}</p>
              {Array.isArray(p.plan_items) ? (
                <ul className="mt-1 space-y-0.5 pl-4">
                  {p.plan_items.map((item, j) => (
                    <li key={j} className="text-sm leading-relaxed list-disc">{item}</li>
                  ))}
                </ul>
              ) : p.plan ? (
                <p className="text-sm leading-relaxed mt-1 pl-4">{p.plan}</p>
              ) : null}
            </div>
          ))}
        </div>
      ) : (fallbackAssessment || fallbackPlan) ? (
        <div className="space-y-2">
          {fallbackAssessment && <p className="text-sm leading-relaxed whitespace-pre-wrap">{fallbackAssessment}</p>}
          {fallbackPlan && <p className="text-sm leading-relaxed whitespace-pre-wrap">{fallbackPlan}</p>}
        </div>
      ) : (
        <p className="text-sm text-muted-foreground/40 italic">No assessment & plan generated yet.</p>
      )}
    </div>
  );
}

export function LabReview({ labs }) {
  if (!labs || labs.length === 0) return null;
  return (
    <div className="mb-5" data-testid="note-section-labs">
      <h4 className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground mb-1">Lab Review</h4>
      <div className="space-y-1">
        {labs.map((lab, i) => (
          <div key={i} className="text-sm flex items-baseline gap-1">
            <span className="font-medium">{lab.test}:</span>
            <span>{lab.value}</span>
            {lab.interpretation && <span className="text-muted-foreground">— {lab.interpretation}</span>}
          </div>
        ))}
      </div>
    </div>
  );
}

export function SharedDecisions({ decisions }) {
  if (!decisions || decisions.length === 0) return null;
  return (
    <div className="mb-5" data-testid="note-section-decisions">
      <h4 className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground mb-1">Shared Decision-Making</h4>
      <ul className="space-y-0.5 pl-4">
        {decisions.map((d, i) => (
          <li key={i} className="text-sm leading-relaxed list-disc">{d}</li>
        ))}
      </ul>
    </div>
  );
}

export function FollowUpInterval({ interval }) {
  if (!interval) return null;
  return (
    <div className="mb-5" data-testid="note-section-followup">
      <h4 className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground mb-1">Follow-Up</h4>
      <p className="text-sm">{interval}</p>
    </div>
  );
}

export function VisitSummary({ text }) {
  if (!text) return null;
  return (
    <div className="mb-5 pt-4 border-t" data-testid="note-visit-summary">
      <h4 className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground mb-1">Visit Summary</h4>
      <p className="text-sm leading-relaxed">{text}</p>
    </div>
  );
}

export function CodingSummary({ primaryCode, diagnosisCodes }) {
  if (!primaryCode) return null;
  return (
    <div className="mb-5 pt-4 border-t" data-testid="note-coding-summary">
      <h4 className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground mb-1">Coding</h4>
      <div className="flex items-center gap-2 flex-wrap">
        <Badge className="font-mono">{primaryCode}</Badge>
        {(diagnosisCodes || []).map((dx, i) => (
          <Badge key={i} variant="outline" className="font-mono text-[10px]">{dx}</Badge>
        ))}
      </div>
    </div>
  );
}

export function ProcedureNoteDisplay({ procedureNote }) {
  if (!procedureNote?.detected || !procedureNote?.rendered_text) return null;
  return (
    <div className="mb-5 pt-4 border-t" data-testid="note-procedure-note">
      <div className="flex items-center gap-2 mb-2">
        <h4 className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground">Procedure Note</h4>
        <Badge variant="outline" className="text-[10px] gap-1 bg-violet-50 text-violet-700 border-violet-200">
          <Scissors className="w-2.5 h-2.5" /> {procedureNote.template_name}
        </Badge>
      </div>
      <div className="text-sm leading-relaxed whitespace-pre-wrap font-mono bg-muted/20 rounded-lg p-4">
        {procedureNote.rendered_text}
      </div>
    </div>
  );
}

/**
 * Full read-only note renderer — renders entire finalized document from snapshot or encounter.
 */
export function ReadOnlyNoteBody({ snapshot, encounter }) {
  // Prefer snapshot (finalized_document), fall back to live encounter data
  if (snapshot) {
    const soap = snapshot.soap_note || {};
    return (
      <>
        <NoteSection label="HPI" text={soap.hpi} />
        <NoteSection label="ROS" text={soap.ros} />
        <NoteSection label="EXAM" text={soap.exam} />
        <AssessmentPlan problems={soap.assessment_plan} />
        <LabReview labs={snapshot.lab_review} />
        <SharedDecisions decisions={snapshot.shared_decisions} />
        <FollowUpInterval interval={snapshot.follow_up_interval} />
        {snapshot.procedure_note && (
          <div className="mb-5 pt-4 border-t" data-testid="note-procedure-note">
            <div className="flex items-center gap-2 mb-2">
              <h4 className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground">Procedure Note</h4>
              {snapshot.procedure_template_name && (
                <Badge variant="outline" className="text-[10px] gap-1 bg-violet-50 text-violet-700 border-violet-200">
                  <Scissors className="w-2.5 h-2.5" /> {snapshot.procedure_template_name}
                </Badge>
              )}
            </div>
            <div className="text-sm leading-relaxed whitespace-pre-wrap font-mono bg-muted/20 rounded-lg p-4">
              {snapshot.procedure_note}
            </div>
          </div>
        )}
        <VisitSummary text={snapshot.visit_summary} />
        <CodingSummary primaryCode={snapshot.coding?.primary_code} diagnosisCodes={snapshot.coding?.diagnosis_codes} />
      </>
    );
  }

  // Fallback: render from live encounter data
  if (!encounter) return null;
  const note = encounter.note || {};
  const outputs = encounter.encounter_outputs || {};
  const hasProblemAP = (note.problem_oriented_ap || []).length > 0;
  const sections = hasProblemAP ? ['hpi', 'ros', 'exam'] : ['hpi', 'ros', 'exam', 'assessment', 'plan'];

  return (
    <>
      {sections.map(s => <NoteSection key={s} label={s} text={note[s]?.text} />)}
      {hasProblemAP && <AssessmentPlan problems={note.problem_oriented_ap} />}
      <LabReview labs={note.lab_review} />
      <SharedDecisions decisions={note.shared_decisions} />
      <FollowUpInterval interval={note.follow_up_interval} />
      <ProcedureNoteDisplay procedureNote={encounter.procedure_note} />
      <VisitSummary text={outputs.visit_summary?.summary_short} />
      <CodingSummary primaryCode={outputs.billing_suggestion?.primary_code} diagnosisCodes={outputs.billing_suggestion?.diagnosis_codes} />
    </>
  );
}
